import type {
	PortalCyberSourceCaptureContextRequestDto,
	PortalCyberSourceCaptureContextResponseDto,
	PortalGatewayConfigResponseDto,
	PublicPaymentLinkErrorModel,
	PublicPaymentLinkModel,
	PublicPaymentLinkPayRequestDto,
	PublicPaymentLinkPayResponseDto,
	PublicPaymentLinkPayResultModel,
	PublicPaymentLinkResponseDto,
	PublicPaymentLinkState,
	PublicPaymentMethodLinkSaveRequestDto,
	PublicPaymentMethodLinkSaveResponseDto,
	PublicPaymentMethodLinkSaveResultModel,
} from '@supply-mover-2-0/shared';
import { PAYMENT_LINK_TOKEN_HEADER } from '@supply-mover-2-0/shared';
import { getPortalTenantHeader } from '../lib/tenantResolver';

/**
 * PD-16674 — the storefront's client for the public payment-link endpoints.
 *
 * Unauthenticated, like the public forms client: these calls cannot use
 * `portalFetch`, which adds the signed-in session and its refresh retry. The
 * link token goes in the `X-Payment-Link-Token` header and never in the API
 * URL, because the API logs request paths; the header is redacted there.
 *
 * `X-Portal-Tenant` picks the tenant database on a workd address and is left
 * off on a custom domain, where the API resolves the tenant from the Origin.
 */

/** A refused call, with the link state when the refusal is a state change. */
export class PaymentLinkRequestError extends Error {
	public constructor(
		message: string,
		public readonly status: number,
		public readonly state: PublicPaymentLinkState | null,
	) {
		super(message);
		this.name = 'PaymentLinkRequestError';
	}
}

const baseUrl = (): string =>
	`${import.meta.env.VITE_API_URL}/api/portal/payment-links/current`;

function headers(token: string, json: boolean): HeadersInit {
	const tenantHeader = getPortalTenantHeader();
	return {
		[PAYMENT_LINK_TOKEN_HEADER]: token,
		...(tenantHeader != null ? { 'X-Portal-Tenant': tenantHeader } : {}),
		...(json ? { 'Content-Type': 'application/json' } : {}),
	};
}

async function request<TResponse>(
	token: string,
	path: string,
	init: { method: 'GET' | 'POST'; body?: unknown },
): Promise<TResponse> {
	const response = await fetch(`${baseUrl()}${path}`, {
		method: init.method,
		headers: headers(token, init.body !== undefined),
		body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
		// The page never sends the storefront's sign-in cookies anywhere.
		credentials: 'omit',
	});

	const json = (await response.json().catch(() => null)) as
		| (TResponse & Partial<PublicPaymentLinkErrorModel>)
		| null;

	if (!response.ok) {
		throw new PaymentLinkRequestError(
			json?.message ??
				(response.status === 429
					? 'Too many attempts. Please wait a few minutes and try again.'
					: 'Something went wrong. Please try again.'),
			response.status,
			json?.state ?? null,
		);
	}
	if (json == null) {
		throw new PaymentLinkRequestError(
			'Something went wrong. Please try again.',
			response.status,
			null,
		);
	}
	return json;
}

export async function fetchPaymentLink(
	token: string,
): Promise<PublicPaymentLinkModel> {
	const response = await request<PublicPaymentLinkResponseDto>(token, '', {
		method: 'GET',
	});
	return response.data;
}

export async function payPaymentLink(
	token: string,
	body: PublicPaymentLinkPayRequestDto,
): Promise<PublicPaymentLinkPayResultModel> {
	const response = await request<PublicPaymentLinkPayResponseDto>(
		token,
		'/pay',
		{ method: 'POST', body },
	);
	return response.data;
}

export async function savePaymentMethodThroughLink(
	token: string,
	body: PublicPaymentMethodLinkSaveRequestDto,
): Promise<PublicPaymentMethodLinkSaveResultModel> {
	const response = await request<PublicPaymentMethodLinkSaveResponseDto>(
		token,
		'/payment-method',
		{ method: 'POST', body },
	);
	return response.data;
}

export async function getPaymentLinkCaptureContext(
	token: string,
	paymentType: PortalCyberSourceCaptureContextRequestDto['paymentType'],
): Promise<string> {
	const body: PortalCyberSourceCaptureContextRequestDto = {
		paymentType,
		targetOrigin: window.location.origin,
	};
	const response = await request<PortalCyberSourceCaptureContextResponseDto>(
		token,
		'/cybersource/capture-context',
		{ method: 'POST', body },
	);
	return response.data.captureContext;
}

/** The PDF of an invoice the link covers, as a Blob. */
export async function fetchPaymentLinkInvoicePdf(
	token: string,
	invoiceId: number,
): Promise<Blob> {
	const response = await fetch(`${baseUrl()}/invoices/${invoiceId}/pdf`, {
		headers: headers(token, false),
		credentials: 'omit',
	});
	if (!response.ok) {
		throw new PaymentLinkRequestError(
			'The invoice could not be downloaded.',
			response.status,
			null,
		);
	}
	return response.blob();
}

/** The gateway configuration the link page already holds, in the form's shape. */
export function toGatewayConfigResponse(
	link: PublicPaymentLinkModel,
): PortalGatewayConfigResponseDto {
	return { success: link.gateway != null, data: link.gateway };
}
